/**
 * Pure helpers extracted from `AssessmentBadge` + `AssessmentForm` so the
 * load-bearing contracts get unit-test coverage without dragging in
 * @testing-library/react (which the lexide test convention avoids — see
 * the comment in `usePlatformOpListener.test.ts`).
 *
 * The components keep the JSX; this module owns the behaviour the kernel
 * writer + the gating ladder depend on:
 *  - the open-vocab acceptance (off-list role survives the build)
 *  - the §13.1(a) `assessed` minimum (role + weight required)
 *  - the skip → declined branch
 *  - the full-set overwrite (last-write-wins)
 */

import type {
  SourceAssessment,
  SourceAssessmentStatus,
  SourceAssessmentWeight,
} from "@/types/gateway";

/* ---------- Badge ---------- */

/**
 * The label shown on an `AssessmentBadge`. When `assessed` AND the
 * lawyer's open-vocab role is present, show the role verbatim — a
 * consultivo / regulatory matter coined `regulatory-source` and the
 * badge must NOT translate or mangle it. Otherwise fall through to the
 * status's i18n label.
 */
export function badgeLabel(
  status: SourceAssessmentStatus,
  role: string | undefined,
  t: (key: string) => string,
): string {
  if (status === "assessed" && role) return role;
  return t(`assessment.status.${status}`);
}

/* ---------- Form ---------- */

export interface AssessmentFormState {
  role: string;
  weight: SourceAssessmentWeight | "";
  relevance: string;
  actors: string;
  notes: string;
}

export interface AssessmentFormValues {
  role: string;
  weight: SourceAssessmentWeight;
  relevance: string;
  actors: string;
  notes: string;
}

export type AssessmentFormSubmit =
  | { status: "assessed"; values: AssessmentFormValues }
  | { status: "declined" };

/**
 * Derive an initial form state from a possibly-`initial` `SourceAssessment`
 * (re-assessment of an existing `.info`) and a fallback `suggestedRole`
 * (the intake classify's guess for a fresh `pending` source). Pre-fills
 * EVERY editable field so an "edit one field" submit doesn't wipe the
 * rest — the kernel writer (`assessSourceInfo`) is last-write-wins
 * overwrite, NOT additive merge.
 */
export function initialFormState(
  initial: Partial<SourceAssessment> | undefined,
  suggestedRole: string | undefined,
): AssessmentFormState {
  return {
    role: initial?.role ?? suggestedRole ?? "",
    weight: initial?.weight ?? "",
    relevance: initial?.relevance ?? "",
    actors: initial?.actors ?? "",
    notes: initial?.notes ?? "",
  };
}

/**
 * Can the form submit as `assessed`? §13.1(a) minimum: role + weight,
 * everything else optional. Trim whitespace on role so a stray space
 * doesn't false-positive.
 */
export function canSubmitAssessed(state: AssessmentFormState): boolean {
  return Boolean(state.role.trim()) && Boolean(state.weight);
}

/**
 * Build the `assessed` submit payload from form state. Trims every
 * text field so trailing spaces don't pollute the `.info` (the writer
 * persists strings verbatim). Throws if the gate `canSubmitAssessed`
 * would refuse — callers should check the gate first.
 */
export function buildAssessedSubmit(state: AssessmentFormState): AssessmentFormSubmit {
  if (!canSubmitAssessed(state)) {
    throw new Error("buildAssessedSubmit: role + weight required");
  }
  return {
    status: "assessed",
    values: {
      role: state.role.trim(),
      // canSubmitAssessed guarantees `weight !== ""`; narrow.
      weight: state.weight as SourceAssessmentWeight,
      relevance: state.relevance.trim(),
      actors: state.actors.trim(),
      notes: state.notes.trim(),
    },
  };
}

/** The Skip / "decide later" action's payload — never forces completion. */
export const DECLINED_SUBMIT: AssessmentFormSubmit = { status: "declined" };
